/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'

import {
  SvgIcon
} from '../'

export class ProgressIndicator extends React.Component {
  static propTypes = {
    steps: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      state: PropTypes.oneOf(["active", "completed", "error"]),
      onClick: PropTypes.func
    })).isRequired,
    vertical: PropTypes.bool,
    displayStepNames: PropTypes.bool,
    shade: PropTypes.bool,  // set to true if using on gray background
    className: PropTypes.string
  }

  static defaultProps = {
    displayStepNames: true,
    vertical: false
  }

  getProgressPercentage = (steps) => {
    let completedSteps = steps.filter(step => step.state === "completed")
    let numberOfCompletedSteps = completedSteps.length - 1

    return numberOfCompletedSteps / (steps.length - 1) * 100
  }

  render() {
    let steps = this.props.steps.map((step, index) => (
      <ProgressStep
        name={step.name}
        state={step.state}
        onClick={step.onClick}
        displayName={this.props.displayStepNames}
        vertical={this.props.vertical}
        key={`progressStep_${index}`}
      />
    ))

    let progressPercentage = this.getProgressPercentage(this.props.steps)
    const { vertical, shade, className } = this.props

    return (
      <div className={`slds-progress ${vertical ? 'slds-progress_vertical' : ''} ${shade ? 'slds-progress_shade' : ''} ${className}`}>
        <ol className='slds-progress__list'>
          {steps}
        </ol>
        <div
          className='slds-progress-bar slds-progress-bar_small'
          aria-valuemin='0'
          aria-valuemax='100'
          aria-valuenow={progressPercentage}
          role='progressbar'>
          <span className='slds-progress-bar__value' style={{ width: progressPercentage+'%' }}>
            <span className='slds-assistive-text'>
              {`Progress: ${progressPercentage}%`}
            </span>
          </span>
        </div>
      </div>
    )
  }
}

class ProgressStep extends React.Component {
  static propTypes = {
    state: PropTypes.oneOf(['active', 'completed', 'error']),  // consider adding 'disabled'
    name: PropTypes.string,
    onClick: PropTypes.func,
    displayName: PropTypes.bool,
    vertical: PropTypes.bool
  }

  static defaultProps = {
    vertical: false
  }

  getStateClassName = () => {
    const stateClassNames = {
      active: 'slds-is-active',
      completed: 'slds-is-completed',
      error: 'slds-has-error'
    }

    return this.props.state
      ? stateClassNames[this.props.state]
      : ''
  }

  handleClick = () => {
    this.setState({
      state: 'active'
    })

    if (this.props.onClick) {
      this.props.onClick()
    }
  }

  getIcon = () => (
    this.props.state === 'completed'
      ? <SvgIcon
          variant='utility'
          symbol='success'
          colour='success'
          buttonIcon={true}
          iconWidth='1.25rem'
          iconHeight='1.25rem'
        />
      : <SvgIcon
          variant='utility'
          symbol='warning'
          colour='error'
          buttonIcon={true}
          iconWidth='1.25rem'
          iconHeight='1.25rem'
        />
  )

  render() {
    let { state, displayName, name, vertical } = this.props
    let buttonIcon =
      state === 'completed' ||
      state === 'error'

    return (
      <li className={`slds-progress__item ${this.getStateClassName()}`}>
        {vertical
          ? <React.Fragment>
              <div className='slds-progress__marker'></div>
              <div className='slds-progress__item_content slds-grid slds-grid_align-spread'>{name}</div>
            </React.Fragment>
          : <button
              className={`slds-button slds-progress__marker ${buttonIcon ? 'slds-button_icon slds-progress__marker_icon' : ''}`}
              title={name}
              onClick={this.handleClick}>
              {buttonIcon &&
                this.getIcon()
              }
              <span className='slds-assistive-text'>{name}</span>
              {displayName &&
                <div className='slds-is-relative'>
                  <div className='slds-p-top_small slds-text-color_default' style={{ position: 'absolute', left: -20 }}>
                    {name}
                  </div>
                </div>
              }
            </button>
        }
      </li>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/progress-indicator/index.js